import React, { useState, useRef } from 'react';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CubeTransparentIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  ArchiveBoxIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../../services/authService';

interface ModalImportarAlmacen3DProps {
  isOpen: boolean;
  onClose: () => void;
  onImportadoExitoso: () => void;
  almacenActualCodigo?: string;
  totalProductosActuales?: number;
}

interface ResumenArchivo {
  nombre: string;
  codigo: string;
  ancho: number;
  largo: number;
  temaPiso: string;
  temaPared: string;
  totalRacks3D: number;
  totalZonas: number;
  totalCapacidades: number;
  totalProductos: number;
  totalUnidades: number;
  tamanoKb: number;
  nombreArchivo: string;
}

export const ModalImportarAlmacen3D: React.FC<ModalImportarAlmacen3DProps> = ({
  isOpen,
  onClose,
  onImportadoExitoso,
  almacenActualCodigo = 'ALM01',
  totalProductosActuales = 0,
}) => {
  const [archivoJson, setArchivoJson] = useState<any | null>(null);
  const [resumen, setResumen] = useState<ResumenArchivo | null>(null);
  const [errorValidacion, setErrorValidacion] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [procesando, setProcesando] = useState(false);

  // Opciones de importación
  const [modo, setModo] = useState<'reemplazar' | 'solo_infraestructura' | 'fusionar'>('reemplazar');
  const [importar3D, setImportar3D] = useState(true);
  const [importarZonas, setImportarZonas] = useState(true);
  const [importarInventario, setImportarInventario] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const procesarTextoJson = (texto: string, nombreArchivo: string, tamanoBytes: number) => {
    try {
      const parsed = JSON.parse(texto);
      
      // Permitir formato estándar de LogixPort o estructura flexible de almacén
      const almacenObj = parsed.almacen || parsed.configuracion || parsed;
      const layoutData = almacenObj.layout_data || parsed.layout_data || {};
      const racks3d = layoutData.racks3d || {};
      const zonas = parsed.zonas_operativas || parsed.zonasOperativas || [];
      const capacidades = parsed.capacidades_rack || parsed.capacidadesRack || [];
      const items = parsed.inventario || parsed.items || parsed.productos || [];

      const totalRacks = typeof racks3d === 'object' ? Object.keys(racks3d).length : 0;
      let totalUnidades = 0;
      if (Array.isArray(items)) {
        totalUnidades = items.reduce((acc: number, it: any) => acc + (Number(it.cantidad) || 0), 0);
      }

      setArchivoJson(parsed);
      setErrorValidacion(null);
      setResumen({
        nombre: almacenObj.nombre || 'Almacén Importado',
        codigo: almacenObj.codigo || 'ALM01',
        ancho: Number(almacenObj.dimension_ancho) || 80,
        largo: Number(almacenObj.dimension_largo) || 80,
        temaPiso: almacenObj.tema_piso || 'concreto',
        temaPared: almacenObj.tema_pared || 'industrial',
        totalRacks3D: totalRacks,
        totalZonas: Array.isArray(zonas) ? zonas.length : 0,
        totalCapacidades: Array.isArray(capacidades) ? capacidades.length : 0,
        totalProductos: Array.isArray(items) ? items.length : 0,
        totalUnidades: Math.round(totalUnidades * 100) / 100,
        tamanoKb: Math.round(tamanoBytes / 1024),
        nombreArchivo,
      });
    } catch (err: any) {
      console.error('Error al analizar archivo JSON:', err);
      setErrorValidacion('El archivo seleccionado no es un JSON válido o está dañado.');
      setArchivoJson(null);
      setResumen(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setErrorValidacion('Por favor selecciona un archivo con extensión .json');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      procesarTextoJson(content, file.name, file.size);
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setArrastrando(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setArrastrando(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setErrorValidacion('Solo se admiten archivos .json');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      procesarTextoJson(content, file.name, file.size);
    };
    reader.readAsText(file);
  };

  const handleEjecutarImportacion = async () => {
    if (!archivoJson || !resumen) {
      toast.error('Carga primero un archivo JSON válido');
      return;
    }

    setProcesando(true);
    try {
      const payload = {
        modo,
        importar_3d: importar3D,
        importar_zonas_capacidades: importarZonas,
        importar_inventario: modo !== 'solo_infraestructura' && importarInventario,
        almacen: archivoJson.almacen || archivoJson.configuracion || archivoJson,
        zonas_operativas: archivoJson.zonas_operativas || archivoJson.zonasOperativas || [],
        capacidades_rack: archivoJson.capacidades_rack || archivoJson.capacidadesRack || [],
        inventario: archivoJson.inventario || archivoJson.items || archivoJson.productos || [],
      };

      const response = await api.post('/api/institucion/almacen/gemelo-digital/importar', payload);

      if (response.data?.ok) {
        toast.success(
          `¡Gemelo digital importado exitosamente! (${response.data.resumen?.racks_3d_importados || resumen.totalRacks3D} racks 3D · ${response.data.resumen?.productos_importados ?? resumen.totalProductos} productos)`
        );
        onImportadoExitoso();
        onClose();
      } else {
        toast.success('Almacén importado con éxito');
        onImportadoExitoso();
        onClose();
      }
    } catch (err: any) {
      console.error('Error al importar almacén:', err);
      const msg = err.response?.data?.detail || err.message || 'Error al procesar la importación';
      toast.error(`Error: ${msg}`);
    } finally {
      setProcesando(false);
    }
  };

  const limpiarArchivo = () => {
    setArchivoJson(null);
    setResumen(null);
    setErrorValidacion(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-black/70 backdrop-blur-md animate-fadeIn">
      <div 
        className="relative w-full max-w-2xl bg-white dark:bg-[#0f1117] border border-slate-200 dark:border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col my-auto transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera iOS Glass */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-white/[0.06] bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <CubeTransparentIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                Importar Gemelo Digital 3D
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                  WMS & Racks
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                Restaura infraestructura 3D, racks interactivos y catálogo de inventario desde un respaldo JSON.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={procesando}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-zinc-200 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido Modal */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
          {/* Zona de Drop o Carga de Archivo */}
          {!resumen ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`group relative flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-3xl cursor-pointer transition-all ${
                arrastrando
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/10 scale-[0.99]'
                  : 'border-slate-300 dark:border-white/10 hover:border-blue-500/60 dark:hover:border-blue-400/40 bg-slate-50/60 dark:bg-white/[0.01] hover:bg-blue-50/30 dark:hover:bg-blue-900/10'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="w-14 h-14 mb-3 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                <DocumentArrowUpIcon className="w-7 h-7" />
              </div>
              <p className="text-sm font-bold text-slate-800 dark:text-zinc-200 text-center">
                Arrastra tu archivo <span className="text-blue-600 dark:text-blue-400 font-mono">.json</span> aquí o haz clic para explorar
              </p>
              <p className="text-xs text-slate-400 dark:text-zinc-500 mt-1 text-center max-w-sm">
                Archivos exportados previamente desde LogixPort o configuraciones de Gemelo Digital 3D de Almacén.
              </p>
              <button
                type="button"
                className="mt-4 px-4 py-2 rounded-xl text-xs font-bold bg-white dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-200 dark:border-white/10 shadow-xs hover:border-blue-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all"
              >
                Elegir Archivo JSON
              </button>
            </div>
          ) : (
            /* Tarjeta Resumen Bento del Archivo Detectado */
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/30 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-blue-600 text-white shrink-0 mt-0.5 shadow-md shadow-blue-500/30">
                    <CheckCircleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                        Archivo Válido Detectado
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300">
                        {resumen.tamanoKb} KB
                      </span>
                    </div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 truncate max-w-md">
                      {resumen.nombreArchivo}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                      Instalación: <strong className="text-slate-700 dark:text-zinc-300">{resumen.nombre}</strong> ({resumen.codigo})
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={limpiarArchivo}
                  className="text-xs font-bold text-slate-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors p-1"
                  title="Cambiar archivo"
                >
                  Cambiar
                </button>
              </div>

              {/* Bento Grid con Datos Cuantitativos */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-slate-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
                    <BuildingOffice2Icon className="w-3.5 h-3.5" />
                    <span>Huella 3D</span>
                  </div>
                  <p className="text-sm font-black text-slate-900 dark:text-white mt-1">
                    {resumen.ancho}m × {resumen.largo}m
                  </p>
                  <p className="text-[10px] text-slate-400 dark:text-zinc-500">
                    {(resumen.ancho * resumen.largo).toLocaleString()} m² nave
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-indigo-500 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wider">
                    <CubeTransparentIcon className="w-3.5 h-3.5" />
                    <span>Racks 3D</span>
                  </div>
                  <p className="text-sm font-black text-slate-900 dark:text-white mt-1">
                    {resumen.totalRacks3D} Racks
                  </p>
                  <p className="text-[10px] text-slate-400 dark:text-zinc-500">
                    Modelado tridimensional
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-amber-500 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider">
                    <SparklesIcon className="w-3.5 h-3.5" />
                    <span>Zonas / Reglas</span>
                  </div>
                  <p className="text-sm font-black text-slate-900 dark:text-white mt-1">
                    {resumen.totalZonas} Zonas
                  </p>
                  <p className="text-[10px] text-slate-400 dark:text-zinc-500">
                    {resumen.totalCapacidades} reglas de rack
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-emerald-500 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                    <ArchiveBoxIcon className="w-3.5 h-3.5" />
                    <span>Inventario</span>
                  </div>
                  <p className="text-sm font-black text-slate-900 dark:text-white mt-1">
                    {resumen.totalProductos} SKUs
                  </p>
                  <p className="text-[10px] text-slate-400 dark:text-zinc-500">
                    {resumen.totalUnidades.toLocaleString()} unidades
                  </p>
                </div>
              </div>

              {/* Selector de Estrategia de Carga */}
              <div className="space-y-2.5 pt-2">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-zinc-300 block">
                  Estrategia de Carga en el Almacén Activo ({almacenActualCodigo})
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {/* Reemplazo Total */}
                  <button
                    type="button"
                    onClick={() => setModo('reemplazar')}
                    className={`p-3 rounded-2xl text-left border transition-all ${
                      modo === 'reemplazar'
                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/30 text-blue-950 dark:text-blue-100 ring-2 ring-blue-500/20'
                        : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 bg-slate-50/30 dark:bg-white/[0.01]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-black">🔄 Reemplazo Total</span>
                      {modo === 'reemplazar' && (
                        <span className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400"></span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight">
                      Carga el gemelo 3D y <strong>sustituye</strong> el inventario existente con los datos del archivo.
                    </p>
                  </button>

                  {/* Solo Infraestructura */}
                  <button
                    type="button"
                    onClick={() => setModo('solo_infraestructura')}
                    className={`p-3 rounded-2xl text-left border transition-all ${
                      modo === 'solo_infraestructura'
                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30 text-indigo-950 dark:text-indigo-100 ring-2 ring-indigo-500/20'
                        : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 bg-slate-50/30 dark:bg-white/[0.01]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-black">🏗️ Solo 3D / Layout</span>
                      {modo === 'solo_infraestructura' && (
                        <span className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400"></span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight">
                      Aplica el nuevo layout 3D y racks, <strong>conservando intactos los productos</strong> ya registrados.
                    </p>
                  </button>

                  {/* Fusionar */}
                  <button
                    type="button"
                    onClick={() => setModo('fusionar')}
                    className={`p-3 rounded-2xl text-left border transition-all ${
                      modo === 'fusionar'
                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-950 dark:text-emerald-100 ring-2 ring-emerald-500/20'
                        : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 bg-slate-50/30 dark:bg-white/[0.01]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-black">➕ Fusionar</span>
                      {modo === 'fusionar' && (
                        <span className="w-2 h-2 rounded-full bg-emerald-600 dark:bg-emerald-400"></span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight">
                      Aplica el layout 3D y <strong>añade los productos</strong> del archivo sumándolos al stock actual.
                    </p>
                  </button>
                </div>
              </div>

              {/* Toggles Granulares */}
              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/10 space-y-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider block">
                  Componentes a importar:
                </span>
                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={importar3D}
                      onChange={(e) => setImportar3D(e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span>Geometría y Racks 3D ({resumen.totalRacks3D})</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={importarZonas}
                      onChange={(e) => setImportarZonas(e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span>Zonas y Reglas de Rack ({resumen.totalZonas})</span>
                  </label>
                  {modo !== 'solo_infraestructura' && (
                    <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 dark:text-zinc-300">
                      <input
                        type="checkbox"
                        checked={importarInventario}
                        onChange={(e) => setImportarInventario(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <span>Catálogo de Productos ({resumen.totalProductos})</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Alerta de Sobrescritura si es Reemplazo Total */}
              {modo === 'reemplazar' && totalProductosActuales > 0 && (
                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                    <strong>Aviso de Reemplazo:</strong> Tu almacén contiene actualmente <strong>{totalProductosActuales} productos</strong>. Al ejecutar la importación, se limpiará ese inventario y se reemplazarán por los <strong>{resumen.totalProductos} productos</strong> del archivo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error de validación */}
          {errorValidacion && (
            <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-600 dark:text-rose-400 font-medium">
              {errorValidacion}
            </div>
          )}
        </div>

        {/* Footer de Acciones */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-white/[0.06] bg-slate-50/50 dark:bg-white/[0.02]">
          <button
            type="button"
            onClick={onClose}
            disabled={procesando}
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleEjecutarImportacion}
            disabled={!resumen || procesando}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:scale-100 disabled:shadow-none cursor-pointer"
          >
            {procesando ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                <span>Importando Gemelo Digital...</span>
              </>
            ) : (
              <>
                <ArrowUpTrayIcon className="w-4 h-4" />
                <span>
                  {resumen
                    ? `Importar Gemelo 3D (${resumen.totalRacks3D} Racks · ${resumen.totalProductos} Prods)`
                    : 'Seleccionar Respaldo'}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
